import Seo from "@/components/Seo";
import { FadeUp, MaskedLines, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, LuxeButton } from "@/components/shared";
import { ArrowRight } from "lucide-react";

const VILLA_IMG = "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/7-1920w.png";
const HERO_IMG = "https://images.unsplash.com/photo-1596178067639-5c6e68aea6dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHx8dHJvcGljYWwlMjBwb29sfGVufDB8fHx8MTc4ODk2MTAzNXww&ixlib=rb-4.1.0&q=85";

const DominicanRepublic = () => (
  <>
    <Seo
      title="Dominican Republic Real Estate & Vacation Villas — Scherif Real Estate"
      siteName="Scherif Real Estate"
      description="The international portfolio of Scherif Real Estate: luxury villas and vacation properties in Cap Cana, Punta Cana, and across the Dominican Republic."
    />

    <section className="relative flex min-h-[85vh] items-end overflow-hidden bg-obsidian" data-testid="dr-hero">
      <img src={HERO_IMG} alt="Oceanfront pool at a Dominican Republic luxury resort" className="absolute inset-0 h-full w-full object-cover opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/20" aria-hidden="true" />
      <div className="container-luxe relative z-10 pb-24 pt-40">
        <Eyebrow dark className="mb-8">International Portfolio</Eyebrow>
        <h1 className="max-w-4xl font-serif text-[12vw] font-light leading-[1.04] tracking-tight text-ivory sm:text-7xl" data-testid="dr-headline">
          <MaskedLines delay={0.3} lines={["The Dominican", "Republic."]} />
        </h1>
        <FadeUp delay={0.9}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/85 sm:text-lg">
            The international extension of Scherif Real Estate — luxury villas, private retreats, and vacation properties across the Caribbean&apos;s
            most celebrated coast.
          </p>
        </FadeUp>
      </div>
    </section>

    <section className="container-luxe py-24 sm:py-36" data-testid="dr-exclusive">
      <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <Eyebrow className="mb-4">Signature Listing</Eyebrow>
            <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-charcoal sm:text-5xl">Luxury Dominican Republic Villa</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Private Pool", "Walk to Beach", "12 Beds"].map((h) => (
                <span key={h} className="border border-champagne/50 bg-gold-tint px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal">
                  {h}
                </span>
              ))}
            </div>
            <p className="mt-8 text-base leading-relaxed text-slate-muted">
              Experience tropical luxury in this spacious Dominican Republic villa. Perfect for large groups or families, this property features a
              private pool, modern amenities, and easy walking access to the beach.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <LuxeButton to="/vacation-homes#inquiry" variant="dark" testId="dr-inquire-btn">Inquire About Availability</LuxeButton>
              <LuxeButton href="tel:+12403724804" variant="outline-dark" testId="dr-call-btn">Call 240-372-4804</LuxeButton>
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-7">
          <div className="img-zoom">
            <img src={VILLA_IMG} alt="Luxury Dominican Republic villa exterior with pool" className="aspect-[16/10] w-full object-cover" loading="lazy" />
          </div>
        </FadeUp>
      </div>
    </section>

    <section className="bg-obsidian py-24 sm:py-32" data-testid="dr-tour">
      <div className="container-luxe">
        <FadeUp className="mb-12 max-w-2xl">
          <Eyebrow dark className="mb-4">Immersive Tour</Eyebrow>
          <h2 className="font-serif text-4xl font-light text-ivory sm:text-5xl">Walk the Property From Anywhere</h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="border border-white/15 bg-white/5 p-3" data-testid="dr-virtual-tour">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ivory">Private Residence — 360° Tour</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">Drag to Explore</p>
            </div>
            <iframe
              src="https://virtualhome360.pt/app/viewer/index.php?code=f076073b2082f8741a9cd07b789c77a0"
              title="Dominican Republic villa — 360 virtual tour"
              className="h-[380px] w-full border-0 sm:h-[480px] lg:h-[620px]"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </FadeUp>
      </div>
    </section>

    <section className="container-luxe py-24 sm:py-32" data-testid="dr-destinations">
      <FadeUp className="mb-14 max-w-2xl">
        <Eyebrow className="mb-4">Where in the DR</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight text-charcoal sm:text-5xl">Curated Enclaves</h2>
      </FadeUp>
      <Stagger className="grid gap-5 md:grid-cols-2">
        {[
          { name: "Cap Cana", img: VILLA_IMG, note: "Marina, golf & villa estates" },
          { name: "Dominican Republic", img: "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/IMG_5529-5c02de18-1920w.webp", note: "The wider island portfolio" },
        ].map((d) => (
          <StaggerItem key={d.name}>
            <div className="img-zoom group relative">
              <img src={d.img} alt={`${d.name} — Dominican Republic real estate`} className="aspect-[16/9] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 p-7">
                <p className="font-serif text-3xl font-light text-ivory">{d.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">{d.note}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <FadeUp className="mt-14">
        <LuxeButton to="/vacation-homes#inquiry" variant="dark" testId="dr-plan-btn">
          Plan Your Escape <ArrowRight size={12} aria-hidden="true" />
        </LuxeButton>
      </FadeUp>
    </section>
  </>
);

export default DominicanRepublic;
